import React, { useState, useMemo } from 'react';
import { 
  FileText, 
  Lightbulb, 
  Code, 
  ExternalLink,
  X,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  HelpCircle,
  History,
  Settings,
  Copy,
  Check,
  BookOpen,
  Network,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { MarkdownRenderer } from './MarkdownRenderer';
import { useThemeStore } from '../../stores/theme.store';
import { 
  DocumentationCompletenessBar, 
  DocumentationSearch, 
  RatingComponent,
  IntegrationDiagram,
} from './TemplateUXComponents';
import { 
  useDocumentationCompleteness, 
  useDocumentationSearch,
  useTemplateRatings,
} from '../../hooks/useProviderTemplateUX';

interface CodeExample {
  id: string;
  language: string;
  title: string;
  description: string;
  code: string;
  operation?: string;
}

interface UseCase {
  id: string;
  title: string;
  description: string;
  businessScenario: string;
  recommendedFor: string[];
  icon?: string;
}

interface ChangelogEntry {
  version: string;
  date: string;
  changes: string[];
}

interface FaqEntry {
  question: string;
  answer: string;
}

interface EnvironmentRequirement {
  environment: 'sandbox' | 'production';
  requirements: string[];
  notes?: string;
}

interface TemplateData {
  id: string;
  name: string;
  description?: string;
  documentation?: string;
  documentationUrl?: string;
  setupGuide?: string;
  codeExamples?: CodeExample[];
  useCases?: UseCase[];
  changelog?: ChangelogEntry[];
  faq?: FaqEntry[];
  knownLimitations?: string[];
  environmentRequirements?: EnvironmentRequirement[];
  complementaryTemplates?: string[];
  category?: string;
  type?: string;
  version?: string;
  config?: {
    baseUrl?: string;
    authConfig?: {
      type: string;
      headerName?: string;
    };
    operations?: Array<{ name: string; method: string; endpoint: string }>;
    webhookConfig?: { enabled: boolean };
  };
}

interface TemplateDocsViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: TemplateData;
}

type TabId = 'documentation' | 'useCases' | 'codeExamples' | 'integration';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'documentation', label: 'Documentação', icon: <FileText className="w-4 h-4" /> },
  { id: 'useCases', label: 'Casos de Uso', icon: <Lightbulb className="w-4 h-4" /> },
  { id: 'codeExamples', label: 'Exemplos de Código', icon: <Code className="w-4 h-4" /> },
  { id: 'integration', label: 'Integração', icon: <Network className="w-4 h-4" /> },
];

const LANGUAGE_COLORS: Record<string, string> = {
  typescript: 'bg-blue-100 text-blue-800',
  javascript: 'bg-yellow-100 text-yellow-800',
  python: 'bg-green-100 text-green-800',
  curl: 'bg-gray-100 text-gray-800',
  java: 'bg-red-100 text-red-800',
  csharp: 'bg-purple-100 text-purple-800',
  go: 'bg-cyan-100 text-cyan-800',
  php: 'bg-indigo-100 text-indigo-800',
};

export function TemplateDocsViewerModal({
  isOpen,
  onClose,
  template,
}: TemplateDocsViewerModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('documentation');
  const [expandedExamples, setExpandedExamples] = useState<Set<string>>(new Set());
  const [expandedFaq, setExpandedFaq] = useState<Set<number>>(new Set());
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const { isDarkMode } = useThemeStore();

  // Documentation completeness
  const completeness = useDocumentationCompleteness(template);

  // Documentation search
  const allDocContent = useMemo(() => {
    return [
      template.documentation || '',
      template.setupGuide || '',
      ...(template.faq?.map(f => `${f.question} ${f.answer}`) || []),
    ].join('\n');
  }, [template]);

  const { matches, matchCount, highlightedContent } = useDocumentationSearch(allDocContent, searchTerm);

  // Ratings
  const { ratings, submitRating, isSubmitting } = useTemplateRatings(template.id);

  const handleSearchNavigate = (direction: 'prev' | 'next') => {
    if (!matchCount || matchCount === 0) return;
    if (direction === 'next') {
      setCurrentMatchIndex((prev) => (prev + 1) % matchCount);
    } else {
      setCurrentMatchIndex((prev) => (prev - 1 + matchCount) % matchCount);
    }
  };

  const toggleExample = (id: string) => {
    const newExpanded = new Set(expandedExamples);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedExamples(newExpanded);
  };

  const toggleFaq = (index: number) => {
    const newExpanded = new Set(expandedFaq);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedFaq(newExpanded);
  };

  const copyToClipboard = async (code: string, id: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Group code examples by operation
  const groupedExamples = (template.codeExamples || []).reduce((acc, example) => {
    const key = example.operation || 'Geral';
    if (!acc[key]) acc[key] = [];
    acc[key].push(example);
    return acc;
  }, {} as Record<string, CodeExample[]>);

  // Theme-aware color classes
  const colors = isDarkMode ? {
    modalBg: 'bg-gray-900',
    headerBorder: 'border-gray-700',
    headerIconBg: 'bg-blue-900/50',
    headerIconColor: 'text-blue-400',
    titleColor: 'text-white',
    subtitleColor: 'text-gray-400',
    closeButtonColor: 'text-gray-400 hover:text-gray-200 hover:bg-gray-800',
    tabsBg: 'bg-gray-800',
    tabsBorder: 'border-gray-700',
    tabActive: 'bg-blue-900/50 text-blue-400',
    tabInactive: 'text-gray-400 hover:bg-gray-700',
    contentBg: 'bg-gray-900',
    footerBg: 'bg-gray-800',
    footerBorder: 'border-gray-700',
    footerText: 'text-gray-400',
    linkColor: 'text-blue-400 hover:text-blue-300',
  } : {
    modalBg: 'bg-white',
    headerBorder: 'border-gray-200',
    headerIconBg: 'bg-blue-100',
    headerIconColor: 'text-blue-600',
    titleColor: 'text-gray-900',
    subtitleColor: 'text-gray-500',
    closeButtonColor: 'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
    tabsBg: 'bg-gray-50',
    tabsBorder: 'border-gray-200',
    tabActive: 'bg-blue-100 text-blue-700',
    tabInactive: 'text-gray-600 hover:bg-gray-100',
    contentBg: 'bg-white',
    footerBg: 'bg-gray-50',
    footerBorder: 'border-gray-200',
    footerText: 'text-gray-500',
    linkColor: 'text-blue-600 hover:text-blue-800',
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center p-4 z-50 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className={`w-full max-w-5xl ${colors.modalBg} rounded-xl shadow-2xl pointer-events-auto flex flex-col max-h-[90vh]`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
              aria-describedby="modal-description"
            >
              {/* Header */}
              <div className={`flex items-center justify-between p-6 border-b ${colors.headerBorder} flex-shrink-0`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 ${colors.headerIconBg} rounded-lg`}>
                    <BookOpen className={`w-5 h-5 ${colors.headerIconColor}`} aria-hidden="true" />
                  </div>
                  <div>
                    <h2 className={`text-xl font-semibold ${colors.titleColor}`} id="modal-title">
                      {template.name}
                    </h2>
                    <p className={`text-sm ${colors.subtitleColor}`} id="modal-description">
                      {template.description || 'Documentação do template'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className={`p-2 rounded-lg ${colors.closeButtonColor} transition-colors`}
                  aria-label="Fechar modal de documentação"
                >
                  <X className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>

              {/* Tabs */}
              <div className={`flex-shrink-0 border-b ${colors.tabsBorder} ${colors.tabsBg}`}>
                <div className="px-6 py-3">
                  {/* Completeness Bar */}
                  <div className="mb-3">
                    <DocumentationCompletenessBar completeness={completeness} showDetails={true} />
                  </div>
                  
                  {/* Search Bar */}
                  <div className="mb-3">
                    <DocumentationSearch
                      onSearch={setSearchTerm}
                      matchCount={matchCount}
                      currentMatch={currentMatchIndex}
                      onNavigateMatch={handleSearchNavigate}
                    />
                  </div>

                  {/* Tabs Navigation */}
                  <nav className="flex gap-1" role="tablist" aria-label="Seções da documentação">
                    {TABS.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeTab === tab.id
                            ? colors.tabActive
                            : colors.tabInactive
                        }`}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        aria-controls={`tabpanel-${tab.id}`}
                        id={`tab-${tab.id}`}
                      >
                        {tab.icon}
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              {/* Tab Content */}
              <div 
                className={`flex-1 overflow-y-auto p-6 ${colors.contentBg}`}
                role="tabpanel"
                id={`tabpanel-${activeTab}`}
                aria-labelledby={`tab-${activeTab}`}
              >
                {activeTab === 'documentation' && (
                  <DocumentationView 
                    template={template} 
                    expandedFaq={expandedFaq} 
                    toggleFaq={toggleFaq} 
                    isDarkMode={isDarkMode}
                    searchTerm={searchTerm}
                  />
                )}

                {activeTab === 'useCases' && (
                  <UseCasesView template={template} isDarkMode={isDarkMode} />
                )}

                {activeTab === 'codeExamples' && (
                  <CodeExamplesView 
                    groupedExamples={groupedExamples}
                    expandedExamples={expandedExamples}
                    toggleExample={toggleExample}
                    copiedCode={copiedCode}
                    copyToClipboard={copyToClipboard}
                    isDarkMode={isDarkMode}
                  />
                )}

                {activeTab === 'integration' && (
                  <IntegrationView template={template} isDarkMode={isDarkMode} />
                )}
              </div>

              {/* Footer */}
              <div className={`flex-shrink-0 px-6 py-4 border-t ${colors.footerBorder} flex items-center justify-between ${colors.footerBg} rounded-b-xl`}>
                <div className={`flex items-center gap-4 text-sm ${colors.footerText}`}>
                  {/* Rating */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs">Avalie:</span>
                    <RatingComponent
                      rating={ratings?.averageRating || 0}
                      totalRatings={ratings?.totalRatings}
                      onRate={(rating) => submitRating({ rating })}
                      size="sm"
                    />
                  </div>
                  
                  {template.documentationUrl && (
                    <a
                      href={template.documentationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-1 ${colors.linkColor} hover:underline`}
                    >
                      <ExternalLink className="w-4 h-4" />
                      Documentação Oficial
                    </a>
                  )}
                </div>
                <Button variant="secondary" onClick={onClose}>
                  Fechar
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

// Documentation View Component
function DocumentationView({ 
  template, 
  expandedFaq, 
  toggleFaq,
  isDarkMode,
  searchTerm,
}: { 
  template: TemplateData; 
  expandedFaq: Set<number>;
  toggleFaq: (index: number) => void;
  isDarkMode: boolean;
  searchTerm?: string;
}) {
  // Theme-aware colors
  const colors = isDarkMode ? {
    sectionTitle: 'text-white',
    sectionIconBlue: 'text-blue-400',
    sectionIconGreen: 'text-green-400',
    sectionIconPurple: 'text-purple-400',
    sectionIconYellow: 'text-yellow-400',
    sectionIconIndigo: 'text-indigo-400',
    sectionIconCyan: 'text-cyan-400',
    docBg: 'bg-gray-800',
    docBorder: 'border-gray-700',
    setupBg: 'bg-green-900/30',
    setupBorder: 'border-green-700',
    sandboxBg: 'bg-yellow-900/30',
    sandboxBorder: 'border-yellow-700',
    productionBg: 'bg-blue-900/30',
    productionBorder: 'border-blue-700',
    limitationsBg: 'bg-yellow-900/30',
    limitationsBorder: 'border-yellow-700',
    limitationsIcon: 'text-yellow-400',
    changelogBg: 'bg-gray-800',
    changelogBorder: 'border-gray-700',
    changelogBadge: 'bg-indigo-900/50 text-indigo-300',
    changelogDate: 'text-gray-400',
    changelogText: 'text-gray-300',
    changelogBullet: 'text-indigo-400',
    faqBorder: 'border-gray-700',
    faqHeaderBg: 'bg-gray-800 hover:bg-gray-700',
    faqQuestion: 'text-white',
    faqAnswerBg: 'bg-gray-900',
    faqAnswerBorder: 'border-gray-700',
    faqAnswerText: 'text-gray-300',
    faqChevron: 'text-gray-500',
    envTitle: 'text-white',
    envText: 'text-gray-300',
    envNotes: 'text-gray-500',
    emptyBg: 'bg-gray-800',
    emptyBorder: 'border-gray-700',
    emptyIcon: 'text-gray-600',
    emptyText: 'text-gray-400',
  } : {
    sectionTitle: 'text-gray-900',
    sectionIconBlue: 'text-blue-600',
    sectionIconGreen: 'text-green-600',
    sectionIconPurple: 'text-purple-600',
    sectionIconYellow: 'text-yellow-600',
    sectionIconIndigo: 'text-indigo-600',
    sectionIconCyan: 'text-cyan-600',
    docBg: 'bg-gray-50',
    docBorder: 'border-gray-200',
    setupBg: 'bg-green-50',
    setupBorder: 'border-green-200',
    sandboxBg: 'bg-yellow-50',
    sandboxBorder: 'border-yellow-200',
    productionBg: 'bg-blue-50',
    productionBorder: 'border-blue-200',
    limitationsBg: 'bg-yellow-50',
    limitationsBorder: 'border-yellow-200',
    limitationsIcon: 'text-yellow-500',
    changelogBg: 'bg-gray-50',
    changelogBorder: 'border-gray-200',
    changelogBadge: 'bg-indigo-100 text-indigo-800',
    changelogDate: 'text-gray-500',
    changelogText: 'text-gray-700',
    changelogBullet: 'text-indigo-400',
    faqBorder: 'border-gray-200',
    faqHeaderBg: 'bg-gray-50 hover:bg-gray-100',
    faqQuestion: 'text-gray-900',
    faqAnswerBg: 'bg-white',
    faqAnswerBorder: 'border-gray-200',
    faqAnswerText: 'text-gray-700',
    faqChevron: 'text-gray-400',
    envTitle: 'text-gray-900',
    envText: 'text-gray-700',
    envNotes: 'text-gray-500',
    emptyBg: 'bg-gray-50',
    emptyBorder: 'border-gray-200',
    emptyIcon: 'text-gray-300',
    emptyText: 'text-gray-500',
  };

  return (
    <div className="space-y-8">
      {/* Main Documentation */}
      {template.documentation ? (
        <section>
          <h3 className={`text-lg font-semibold ${colors.sectionTitle} mb-4 flex items-center gap-2`}>
            <FileText className={`w-5 h-5 ${colors.sectionIconBlue}`} />
            Documentação
          </h3>
          <div className={`${colors.docBg} rounded-lg p-6 border ${colors.docBorder}`}>
            <MarkdownRenderer content={template.documentation} darkMode={isDarkMode} />
          </div>
        </section>
      ) : (
        <div className={`text-center py-8 ${colors.emptyBg} rounded-lg border ${colors.emptyBorder}`}>
          <FileText className={`w-12 h-12 ${colors.emptyIcon} mx-auto mb-3`} />
          <p className={colors.emptyText}>Documentação não disponível</p>
        </div>
      )}

      {/* Setup Guide */}
      {template.setupGuide && (
        <section>
          <h3 className={`text-lg font-semibold ${colors.sectionTitle} mb-4 flex items-center gap-2`}>
            <Settings className={`w-5 h-5 ${colors.sectionIconGreen}`} />
            Guia de Configuração
          </h3>
          <div className={`${colors.setupBg} rounded-lg p-6 border ${colors.setupBorder}`}>
            <MarkdownRenderer content={template.setupGuide} darkMode={isDarkMode} />
          </div>
        </section>
      )}

      {/* Environment Requirements */}
      {template.environmentRequirements && template.environmentRequirements.length > 0 && (
        <section>
          <h3 className={`text-lg font-semibold ${colors.sectionTitle} mb-4 flex items-center gap-2`}>
            <Settings className={`w-5 h-5 ${colors.sectionIconPurple}`} />
            Requisitos por Ambiente
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {template.environmentRequirements.map((env, index) => (
              <div 
                key={index} 
                className={`rounded-lg p-4 border ${
                  env.environment === 'sandbox' 
                    ? `${colors.sandboxBg} ${colors.sandboxBorder}` 
                    : `${colors.productionBg} ${colors.productionBorder}`
                }`}
              >
                <h4 className={`font-medium ${colors.envTitle} mb-2`}>
                  {env.environment === 'sandbox' ? '🧪 Sandbox' : '🚀 Produção'}
                </h4>
                <ul className="space-y-1">
                  {env.requirements.map((req, reqIndex) => (
                    <li key={reqIndex} className={`text-sm ${colors.envText} flex items-start gap-2`}>
                      <span className="text-gray-400">•</span>
                      {req}
                    </li>
                  ))}
                </ul>
                {env.notes && (
                  <p className={`text-xs ${colors.envNotes} mt-2 italic`}>{env.notes}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Known Limitations */}
      {template.knownLimitations && template.knownLimitations.length > 0 && (
        <section>
          <h3 className={`text-lg font-semibold ${colors.sectionTitle} mb-4 flex items-center gap-2`}>
            <AlertTriangle className={`w-5 h-5 ${colors.sectionIconYellow}`} />
            Limitações Conhecidas
          </h3>
          <div className={`${colors.limitationsBg} rounded-lg p-4 border ${colors.limitationsBorder}`}>
            <ul className="space-y-2">
              {template.knownLimitations.map((limitation, index) => (
                <li key={index} className={`text-sm ${colors.envText} flex items-start gap-2`}>
                  <AlertTriangle className={`w-4 h-4 ${colors.limitationsIcon} flex-shrink-0 mt-0.5`} />
                  {limitation}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Changelog */}
      {template.changelog && template.changelog.length > 0 && (
        <section>
          <h3 className={`text-lg font-semibold ${colors.sectionTitle} mb-4 flex items-center gap-2`}>
            <History className={`w-5 h-5 ${colors.sectionIconIndigo}`} />
            Histórico de Versões
          </h3>
          <div className="space-y-3">
            {template.changelog.map((entry, index) => (
              <div key={index} className={`${colors.changelogBg} rounded-lg p-4 border ${colors.changelogBorder}`}>
                <div className="flex items-center gap-3 mb-2">
                  <Badge className={colors.changelogBadge}>v{entry.version}</Badge>
                  <span className={`text-sm ${colors.changelogDate}`}>{entry.date}</span>
                </div>
                <ul className="space-y-1">
                  {entry.changes.map((change, changeIndex) => (
                    <li key={changeIndex} className={`text-sm ${colors.changelogText} flex items-start gap-2`}>
                      <span className={colors.changelogBullet}>•</span>
                      {change}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {template.faq && template.faq.length > 0 && (
        <section>
          <h3 className={`text-lg font-semibold ${colors.sectionTitle} mb-4 flex items-center gap-2`}>
            <HelpCircle className={`w-5 h-5 ${colors.sectionIconCyan}`} />
            Perguntas Frequentes
          </h3>
          <div className="space-y-2">
            {template.faq.map((entry, index) => (
              <div key={index} className={`border ${colors.faqBorder} rounded-lg overflow-hidden`}>
                <button
                  onClick={() => toggleFaq(index)}
                  className={`w-full flex items-center justify-between p-4 ${colors.faqHeaderBg} transition-colors text-left`}
                >
                  <span className={`font-medium ${colors.faqQuestion}`}>{entry.question}</span>
                  {expandedFaq.has(index) ? (
                    <ChevronDown className={`w-5 h-5 ${colors.faqChevron}`} />
                  ) : (
                    <ChevronRight className={`w-5 h-5 ${colors.faqChevron}`} />
                  )}
                </button>
                {expandedFaq.has(index) && (
                  <div className={`p-4 ${colors.faqAnswerBg} border-t ${colors.faqAnswerBorder}`}>
                    <p className={colors.faqAnswerText}>{entry.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// Use Cases View Component
function UseCasesView({ template, isDarkMode }: { template: TemplateData; isDarkMode: boolean }) {
  const colors = isDarkMode ? {
    emptyBg: 'bg-gray-800',
    emptyBorder: 'border-gray-700',
    emptyIcon: 'text-gray-600',
    emptyText: 'text-gray-400',
    cardBg: 'bg-gray-800',
    cardBorder: 'border-gray-700 hover:border-blue-500',
    cardIconBg: 'bg-blue-900/50',
    cardIconColor: 'text-blue-400',
    cardTitle: 'text-white',
    cardDescription: 'text-gray-400',
    scenarioBg: 'bg-gray-900',
    scenarioLabel: 'text-gray-500',
    scenarioText: 'text-gray-300',
    recommendedLabel: 'text-gray-500',
    recommendedBadge: 'bg-green-900/50 text-green-300',
  } : {
    emptyBg: 'bg-gray-50',
    emptyBorder: 'border-gray-200',
    emptyIcon: 'text-gray-300',
    emptyText: 'text-gray-500',
    cardBg: 'bg-white',
    cardBorder: 'border-gray-200 hover:border-blue-300',
    cardIconBg: 'bg-blue-100',
    cardIconColor: 'text-blue-600',
    cardTitle: 'text-gray-900',
    cardDescription: 'text-gray-600',
    scenarioBg: 'bg-gray-50',
    scenarioLabel: 'text-gray-500',
    scenarioText: 'text-gray-700',
    recommendedLabel: 'text-gray-500',
    recommendedBadge: 'bg-green-100 text-green-800',
  };

  if (!template.useCases || template.useCases.length === 0) {
    return (
      <div className={`text-center py-12 ${colors.emptyBg} rounded-lg border ${colors.emptyBorder}`}>
        <Lightbulb className={`w-12 h-12 ${colors.emptyIcon} mx-auto mb-3`} />
        <p className={colors.emptyText}>Nenhum caso de uso documentado</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {template.useCases.map((useCase) => (
        <div 
          key={useCase.id} 
          className={`${colors.cardBg} rounded-lg p-5 border ${colors.cardBorder} hover:shadow-md transition-all`}
        >
          <div className="flex items-start gap-3 mb-3">
            <div className={`p-2 ${colors.cardIconBg} rounded-lg`}>
              <Lightbulb className={`w-5 h-5 ${colors.cardIconColor}`} />
            </div>
            <div>
              <h4 className={`font-semibold ${colors.cardTitle}`}>{useCase.title}</h4>
              <p className={`text-sm ${colors.cardDescription} mt-1`}>{useCase.description}</p>
            </div>
          </div>
          
          <div className={`${colors.scenarioBg} rounded-lg p-3 mb-3`}>
            <p className={`text-xs ${colors.scenarioLabel} uppercase tracking-wide mb-1`}>Cenário de Negócio</p>
            <p className={`text-sm ${colors.scenarioText}`}>{useCase.businessScenario}</p>
          </div>

          {useCase.recommendedFor.length > 0 && (
            <div>
              <p className={`text-xs ${colors.recommendedLabel} uppercase tracking-wide mb-2`}>Recomendado para</p>
              <div className="flex flex-wrap gap-1">
                {useCase.recommendedFor.map((item, index) => (
                  <Badge key={index} className={`${colors.recommendedBadge} text-xs`}>
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Code Examples View Component
function CodeExamplesView({ 
  groupedExamples,
  expandedExamples,
  toggleExample,
  copiedCode,
  copyToClipboard,
  isDarkMode,
}: { 
  groupedExamples: Record<string, CodeExample[]>;
  expandedExamples: Set<string>;
  toggleExample: (id: string) => void;
  copiedCode: string | null;
  copyToClipboard: (code: string, id: string) => void;
  isDarkMode: boolean;
}) {
  const colors = isDarkMode ? {
    emptyBg: 'bg-gray-800',
    emptyBorder: 'border-gray-700',
    emptyIcon: 'text-gray-600',
    emptyText: 'text-gray-400',
    groupBorder: 'border-gray-700',
    groupHeaderBg: 'bg-gray-800',
    groupHeaderBorder: 'border-gray-700',
    groupHeaderText: 'text-white',
    exampleDivider: 'divide-gray-700',
    exampleBg: 'bg-gray-900',
    exampleHover: 'hover:bg-gray-800',
    chevronColor: 'text-gray-500',
    titleColor: 'text-white',
    descriptionColor: 'text-gray-400',
    copyButtonBg: 'bg-gray-700 hover:bg-gray-600',
    copyButtonText: 'text-gray-300 hover:text-white',
    codeBg: 'bg-gray-950',
    codeText: 'text-gray-100',
  } : {
    emptyBg: 'bg-gray-50',
    emptyBorder: 'border-gray-200',
    emptyIcon: 'text-gray-300',
    emptyText: 'text-gray-500',
    groupBorder: 'border-gray-200',
    groupHeaderBg: 'bg-gray-100',
    groupHeaderBorder: 'border-gray-200',
    groupHeaderText: 'text-gray-900',
    exampleDivider: 'divide-gray-200',
    exampleBg: 'bg-white',
    exampleHover: 'hover:bg-gray-50',
    chevronColor: 'text-gray-400',
    titleColor: 'text-gray-900',
    descriptionColor: 'text-gray-500',
    copyButtonBg: 'bg-gray-700 hover:bg-gray-600',
    copyButtonText: 'text-gray-300 hover:text-white',
    codeBg: 'bg-gray-900',
    codeText: 'text-gray-100',
  };

  // Language badge colors (same for both themes as they're accent colors)
  const LANGUAGE_COLORS_THEMED: Record<string, string> = isDarkMode ? {
    typescript: 'bg-blue-900/50 text-blue-300',
    javascript: 'bg-yellow-900/50 text-yellow-300',
    python: 'bg-green-900/50 text-green-300',
    curl: 'bg-gray-700 text-gray-300',
    java: 'bg-red-900/50 text-red-300',
    csharp: 'bg-purple-900/50 text-purple-300',
    go: 'bg-cyan-900/50 text-cyan-300',
    php: 'bg-indigo-900/50 text-indigo-300',
  } : LANGUAGE_COLORS;

  if (Object.keys(groupedExamples).length === 0) {
    return (
      <div className={`text-center py-12 ${colors.emptyBg} rounded-lg border ${colors.emptyBorder}`}>
        <Code className={`w-12 h-12 ${colors.emptyIcon} mx-auto mb-3`} />
        <p className={colors.emptyText}>Nenhum exemplo de código disponível</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {Object.entries(groupedExamples).map(([operation, examples]) => (
        <div key={operation} className={`border ${colors.groupBorder} rounded-lg overflow-hidden`}>
          {/* Operation Header */}
          <div className={`${colors.groupHeaderBg} px-4 py-3 border-b ${colors.groupHeaderBorder}`}>
            <h4 className={`font-medium ${colors.groupHeaderText}`}>
              {operation === 'Geral' ? '📦 Exemplos Gerais' : `🔧 ${operation}`}
            </h4>
          </div>

          {/* Examples */}
          <div className={`divide-y ${colors.exampleDivider}`}>
            {examples.map((example) => {
              const isExpanded = expandedExamples.has(example.id);

              return (
                <div key={example.id} className={colors.exampleBg}>
                  {/* Example Header */}
                  <div
                    className={`px-4 py-3 flex items-center gap-3 cursor-pointer ${colors.exampleHover} transition-colors`}
                    onClick={() => toggleExample(example.id)}
                  >
                    <button type="button" className={colors.chevronColor}>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                    </button>

                    <Badge className={LANGUAGE_COLORS_THEMED[example.language] || (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800')}>
                      {example.language}
                    </Badge>

                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${colors.titleColor} truncate`}>{example.title}</p>
                      <p className={`text-sm ${colors.descriptionColor} truncate`}>{example.description}</p>
                    </div>
                  </div>

                  {/* Expanded Code */}
                  {isExpanded && (
                    <div className="px-4 pb-4">
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(example.code, example.id);
                          }}
                          className={`absolute right-2 top-2 p-2 ${colors.copyButtonBg} rounded ${colors.copyButtonText} transition-colors z-10`}
                          title="Copiar código"
                        >
                          {copiedCode === example.id ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        <pre className={`${colors.codeBg} ${colors.codeText} p-4 rounded-lg overflow-x-auto text-sm font-mono`}>
                          <code>{example.code}</code>
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default TemplateDocsViewerModal;

// Integration View Component
function IntegrationView({ template, isDarkMode }: { template: TemplateData; isDarkMode: boolean }) {
  const colors = isDarkMode ? {
    sectionTitle: 'text-white',
    sectionIconBlue: 'text-blue-400',
    cardBg: 'bg-gray-800',
    cardBorder: 'border-gray-700',
    cardTitle: 'text-white',
    cardText: 'text-gray-300',
    labelText: 'text-gray-500',
    badgeBg: 'bg-blue-900/50 text-blue-300',
    methodGet: 'bg-green-900/50 text-green-300',
    methodPost: 'bg-blue-900/50 text-blue-300',
    methodPut: 'bg-yellow-900/50 text-yellow-300',
    methodDelete: 'bg-red-900/50 text-red-300',
  } : {
    sectionTitle: 'text-gray-900',
    sectionIconBlue: 'text-blue-600',
    cardBg: 'bg-gray-50',
    cardBorder: 'border-gray-200',
    cardTitle: 'text-gray-900',
    cardText: 'text-gray-700',
    labelText: 'text-gray-500',
    badgeBg: 'bg-blue-100 text-blue-800',
    methodGet: 'bg-green-100 text-green-800',
    methodPost: 'bg-blue-100 text-blue-800',
    methodPut: 'bg-yellow-100 text-yellow-800',
    methodDelete: 'bg-red-100 text-red-800',
  };

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET': return colors.methodGet;
      case 'POST': return colors.methodPost;
      case 'PUT': case 'PATCH': return colors.methodPut;
      case 'DELETE': return colors.methodDelete;
      default: return colors.badgeBg;
    }
  };

  return (
    <div className="space-y-6">
      {/* Integration Diagram */}
      <IntegrationDiagram template={template} />

      {/* API Operations */}
      {template.config?.operations && template.config.operations.length > 0 && (
        <section>
          <h3 className={`text-lg font-semibold ${colors.sectionTitle} mb-4 flex items-center gap-2`}>
            <Code className={`w-5 h-5 ${colors.sectionIconBlue}`} />
            Operações da API ({template.config.operations.length})
          </h3>
          <div className={`${colors.cardBg} rounded-lg border ${colors.cardBorder} overflow-hidden`}>
            <div className="divide-y divide-gray-700">
              {template.config.operations.map((op, index) => (
                <div key={index} className="p-4 flex items-center gap-4">
                  <Badge className={`${getMethodColor(op.method)} font-mono text-xs min-w-[60px] justify-center`}>
                    {op.method}
                  </Badge>
                  <div className="flex-1">
                    <p className={`font-medium ${colors.cardTitle}`}>{op.name}</p>
                    <p className={`text-sm ${colors.cardText} font-mono`}>{op.endpoint}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Authentication Info */}
      {template.config?.authConfig && (
        <section>
          <h3 className={`text-lg font-semibold ${colors.sectionTitle} mb-4 flex items-center gap-2`}>
            <Settings className={`w-5 h-5 ${colors.sectionIconBlue}`} />
            Autenticação
          </h3>
          <div className={`${colors.cardBg} rounded-lg border ${colors.cardBorder} p-4`}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className={`text-xs ${colors.labelText} uppercase tracking-wide mb-1`}>Tipo</p>
                <Badge className={colors.badgeBg}>{template.config.authConfig.type}</Badge>
              </div>
              {template.config.authConfig.headerName && (
                <div>
                  <p className={`text-xs ${colors.labelText} uppercase tracking-wide mb-1`}>Header</p>
                  <code className={`text-sm ${colors.cardText} font-mono`}>{template.config.authConfig.headerName}</code>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Base URL */}
      {template.config?.baseUrl && (
        <section>
          <h3 className={`text-lg font-semibold ${colors.sectionTitle} mb-4 flex items-center gap-2`}>
            <Network className={`w-5 h-5 ${colors.sectionIconBlue}`} />
            URL Base
          </h3>
          <div className={`${colors.cardBg} rounded-lg border ${colors.cardBorder} p-4`}>
            <code className={`text-sm ${colors.cardText} font-mono`}>{template.config.baseUrl}</code>
          </div>
        </section>
      )}
    </div>
  );
}
